// src/modules/components/Header/index.js
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FaChevronDown, FaBars, FaTimes, FaCheck, FaGoogle, FaCog, FaSignOutAlt } from 'react-icons/fa';

// Contexto de tradução GLOBAL
import { useTranslation } from '../../../contexts/LanguageContext';

// Contexto do Modal de Contato
import { useContactModal } from '../../../contexts/ContactModalContext';

// Contexto de Autenticação
import { useAuth } from '../../../contexts/AuthContext';

// Contexto de Transição de Página
import { usePageTransition } from '../../../contexts/PageTransitionContext';

// Assets
import logoVega from '../../../assets/logovega.svg';
import flagBR from '../../../assets/flags/flag-br.svg';
import flagUS from '../../../assets/flags/flag-us.svg';
import flagES from '../../../assets/flags/flag-es.svg';

// Styles
import {
  HeaderWrapper,
  TopBar,
  TopBarInner,
  TopBarLeft,
  TopBarRight,
  TopBarLink,
  LanguageSelector,
  LanguageButton,
  LanguageDropdown,
  LanguageOption,
  FlagIcon,
  HeaderContainer,
  HeaderInner,
  LogoLink,
  NavActions,
  Nav,
  NavItem,
  NavLink,
  NavLinkScroll,
  ContactButton,
  MobileToggle,
  MobileMenu,
  MobileNavLink,
  MobileNavLinkScroll,
  MobileExternalLink,
  MobileContactButton,
  MobileLanguageSelector,
  MobileLanguageOption,
  ProgressBarContainer,
  ProgressBarFill,
  ProgressBarIndeterminate,
  LoginButton,
  UserMenuContainer,
  UserAvatar,
  UserDropdown,
  UserInfo,
  UserMenuItem,
  UserMenuLink,
} from './styles';

// ============================================================
// PROGRESS BAR CONTEXT
// ============================================================
const ProgressContext = createContext(null);

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

export const ProgressProvider = ({ children }) => {
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);

  const start = useCallback(() => {
    setIsLoading(true);
    setIsIndeterminate(false);
    setProgress(10);
  }, []);

  const set = useCallback((value) => {
    setProgress(Math.min(Math.max(value, 0), 100));
  }, []);

  const inc = useCallback((amount = 10) => {
    setProgress(prev => {
      const newProgress = prev + amount;
      if (prev >= 90) return Math.min(prev + 1, 99);
      if (prev >= 70) return Math.min(prev + 2, 90);
      return Math.min(newProgress, 70);
    });
  }, []);

  const done = useCallback(() => {
    setProgress(100);
    setTimeout(() => {
      setIsLoading(false);
      setProgress(0);
    }, 300);
  }, []);

  const startIndeterminate = useCallback(() => {
    setIsLoading(true);
    setIsIndeterminate(true);
  }, []);

  const stop = useCallback(() => {
    setIsLoading(false);
    setIsIndeterminate(false);
    setProgress(0);
  }, []);

  return (
    <ProgressContext.Provider value={{ progress, isLoading, isIndeterminate, start, set, inc, done, startIndeterminate, stop }}>
      {children}
    </ProgressContext.Provider>
  );
};

// ============================================================
// PROGRESS BAR COMPONENT
// ============================================================
const ProgressBar = () => {
  const { progress, isLoading, isIndeterminate } = useProgress();
  if (!isLoading) return null;

  return (
    <ProgressBarContainer>
      {isIndeterminate ? (
        <ProgressBarIndeterminate />
      ) : (
        <ProgressBarFill $progress={progress} $isFinishing={progress === 100} />
      )}
    </ProgressBarContainer>
  );
};

// ============================================================
// FLAG MAPPING
// ============================================================
const flagMap = {
  pt: flagBR,
  en: flagUS,
  es: flagES,
};

// ============================================================
// HEADER COMPONENT
// ============================================================
export const Header = () => {
  // Hook de tradução GLOBAL
  const { t, language, setLanguage, languages } = useTranslation();

  // Hook do modal de contato
  const { openModal } = useContactModal();

  // Hook de autenticação
  const { user, loading, isAdmin, signInWithGoogle, signOut } = useAuth();

  // Hook de transição de página
  const { startTransition } = usePageTransition();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Detecta se está no subdomínio blog (apenas blog deve ir para vegarobotics.com.br)
  const isBlogSubdomain = () => {
    const hostname = window.location.hostname;
    return hostname === 'blog.vegarobotics.com.br';
  };

  // URL do domínio principal
  const mainDomainUrl = 'https://www.vegarobotics.com.br';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = () => {
      setIsLangOpen(false);
      setIsUserMenuOpen(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  const scrollToSection = (sectionId, e) => {
    e.preventDefault();

    // Se estiver no blog subdomain, redireciona para o site principal com o hash
    if (isBlogSubdomain()) {
      window.location.href = `${mainDomainUrl}/#${sectionId}`;
      return;
    }

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 120;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }

    setIsMobileOpen(false);
  };

  const toggleLangDropdown = (e) => {
    e.stopPropagation();
    setIsLangOpen(!isLangOpen);
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setIsLangOpen(false);
  };

  const toggleUserMenu = (e) => {
    e.stopPropagation();
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogin = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Erro no login:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Erro no logout:', error);
    }
  };

  const handleCADProClick = (e) => {
    e.preventDefault();
    setIsMobileOpen(false);
    startTransition('cadpro', 'https://cadpro.vegarobotics.com.br', true);
  };

  const currentLanguage = languages.find(l => l.code === language);

  return (
    <>
      <HeaderWrapper>
        <TopBar>
          <TopBarInner>
            {/* Lado esquerdo - Links Carreiras e Investidores */}
            <TopBarLeft>
              {!isBlogSubdomain() && (
                <>
                  <TopBarLink as={Link} to="/carreiras">
                    {t('nav.careers')}
                  </TopBarLink>
                  <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 8px' }}>|</span>
                  <TopBarLink as={Link} to="/investidores" style={{ opacity: 0.7 }}>
                    Investidores
                  </TopBarLink>
                  <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 8px' }}>|</span>
                  <TopBarLink as="a" href="https://shop.vegarobotics.com.br">
                    Shop
                  </TopBarLink>
                </>
              )}
            </TopBarLeft>

            {/* Lado direito - Idioma e Login */}
            <TopBarRight>
              <LanguageSelector>
                <LanguageButton
                  onClick={toggleLangDropdown}
                  className={isLangOpen ? 'open' : ''}
                  aria-label={t('accessibility.toggleLanguage')}
                  aria-expanded={isLangOpen}
                >
                  <FlagIcon src={flagMap[language]} alt={currentLanguage?.label} />
                  <span>{language.toUpperCase()}</span>
                  <FaChevronDown className="chevron" />
                </LanguageButton>

                <LanguageDropdown $isOpen={isLangOpen} role="menu">
                  {languages.map((lang) => (
                    <LanguageOption
                      key={lang.code}
                      $active={language === lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      role="menuitem"
                    >
                      <FlagIcon src={flagMap[lang.code]} alt={lang.label} />
                      <span>{lang.label}</span>
                      <FaCheck className="check" />
                    </LanguageOption>
                  ))}
                </LanguageDropdown>
              </LanguageSelector>

              {/* Login / User Menu - não mostra no Blog */}
              {!isBlogSubdomain() && (
                <>
                  {user ? (
                    <UserMenuContainer>
                      <UserAvatar
                        onClick={toggleUserMenu}
                        className={isUserMenuOpen ? 'open' : ''}
                      >
                        <img
                          src={user.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.user_metadata?.full_name || user.email)}&background=333&color=fff`}
                          alt={user.user_metadata?.full_name || 'User'}
                        />
                        <FaChevronDown className="chevron" />
                      </UserAvatar>

                      <UserDropdown $isOpen={isUserMenuOpen}>
                        <UserInfo>
                          <div className="name">{user.user_metadata?.full_name || 'Usuário'}</div>
                          <div className="email">{user.email}</div>
                        </UserInfo>

                        {isAdmin && (
                          <UserMenuLink to="/admin" onClick={() => setIsUserMenuOpen(false)}>
                            <FaCog />
                            Painel Admin
                          </UserMenuLink>
                        )}

                        <UserMenuItem onClick={handleLogout} className="danger">
                          <FaSignOutAlt />
                          Sair
                        </UserMenuItem>
                      </UserDropdown>
                    </UserMenuContainer>
                  ) : (
                    <LoginButton onClick={handleLogin}>
                      <FaGoogle />
                      Entrar
                    </LoginButton>
                  )}
                </>
              )}
            </TopBarRight>
          </TopBarInner>
        </TopBar>

        <HeaderContainer $isScrolled={isScrolled}>
          <ProgressBar />
          
          <HeaderInner>
            {isBlogSubdomain() ? (
              <LogoLink as="a" href={mainDomainUrl}>
                <img src={logoVega} alt={t('general.company')} />
              </LogoLink>
            ) : (
              <LogoLink to="/" onClick={() => window.scrollTo(0, 0)}>
                <img src={logoVega} alt={t('general.company')} />
              </LogoLink>
            )}

            <NavActions>
              <Nav>
                <NavItem>
                  <NavLinkScroll href="#solucoes" onClick={(e) => scrollToSection('solucoes', e)}>
                    {t('nav.solutions')}
                  </NavLinkScroll>
                </NavItem>
                <NavItem>
                  <NavLinkScroll href="#depoimentos" onClick={(e) => scrollToSection('depoimentos', e)}>
                    {t('nav.testimonials')}
                  </NavLinkScroll>
                </NavItem>
                <NavItem>
                  <NavLink as="a" href="https://blog.vegarobotics.com.br">
                    {t('nav.blog')}
                  </NavLink>
                </NavItem>
              </Nav>

              <ContactButton onClick={openModal}>
                {t('general.contactUs')}
              </ContactButton>
            </NavActions>

            <MobileToggle 
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              aria-label={isMobileOpen ? t('accessibility.closeMenu') : t('accessibility.openMenu')}
            >
              {isMobileOpen ? <FaTimes /> : <FaBars />}
            </MobileToggle>
          </HeaderInner>
        </HeaderContainer>
      </HeaderWrapper>

      <MobileMenu $isOpen={isMobileOpen}>
        <MobileNavLink to="/carreiras">{t('nav.careers')}</MobileNavLink>
        <MobileNavLink to="/investidores">Investidores</MobileNavLink>
        <MobileExternalLink href="https://shop.vegarobotics.com.br">Shop</MobileExternalLink>
        <MobileNavLinkScroll href="#solucoes" onClick={(e) => scrollToSection('solucoes', e)}>
          {t('nav.solutions')}
        </MobileNavLinkScroll>
        <MobileNavLinkScroll href="#depoimentos" onClick={(e) => scrollToSection('depoimentos', e)}>
          {t('nav.testimonials')}
        </MobileNavLinkScroll>
        <MobileNavLink as="a" href="https://blog.vegarobotics.com.br">
          {t('nav.blog')}
        </MobileNavLink>
        <MobileContactButton onClick={() => { openModal(); setIsMobileOpen(false); }}>
          {t('general.contactUs')}
        </MobileContactButton>
        
        <MobileLanguageSelector>
          {languages.map((lang) => (
            <MobileLanguageOption
              key={lang.code}
              $active={language === lang.code}
              onClick={() => handleLanguageChange(lang.code)}
            >
              <FlagIcon src={flagMap[lang.code]} alt={lang.label} />
              {lang.code.toUpperCase()}
            </MobileLanguageOption>
          ))}
        </MobileLanguageSelector>
      </MobileMenu>
    </>
  );
};

export { SkeletonText, SkeletonTitle, SkeletonImage, SkeletonCircle, SkeletonCard, SkeletonButton } from './styles';
export default Header;